import { ConfirmOptions } from "material-ui-confirm";
import { Theme } from "@mui/material";

/**
 * House styling for `useConfirm()` (material-ui-confirm).
 *
 * The library ships a stock MUI dialog — white paper, tight padding, uppercase blue text
 * buttons — which is nothing like the app's own modals. On the profile screens that is
 * plainly visible: BookingInterestManagement renders `<Modal />` AND calls `confirm()`, so
 * the two sat side by side looking like they came from different products.
 *
 * So these options rebuild the confirm dialog out of the same measurements as
 * `Modal.styles.ts` and the `Modals.*` variants in `ModalContent.tsx`: the 1rem-radius
 * neutral.900 paper with its heavy shadow and 3rem padding, the centred bold title, the
 * neutral.500 body, and a pair of pill buttons — the amber→red gradient for the confirming
 * action, transparent for the cancel. Set once on the provider in `_app.tsx`, so every
 * confirm in the app follows without its callers passing anything.
 *
 * Only the look is set here. Copy stays with the caller: the library's own "Are you sure?"
 * / "Ok" / "Cancel" defaults still apply, and a call site that words it differently keeps
 * doing so.
 *
 * Buttons: the provider renders MUI `Button`s, so the shared `<Button>` component cannot be
 * dropped in — `sx` below reproduces its `primary` + `gradient3` and `tertiary` variants
 * (`Button.variants.tsx`) at size `md`.
 */

const MOBILE = "@media (max-width: 600px)";

const paperSx = (theme: Theme) => ({
  ".MuiPaper-root": {
    borderRadius: "1rem",
    boxShadow: "0px 16px 64px 0px #000",
    backgroundColor: theme.colors["neutral.900"],
    backgroundImage: "none",
    fontFamily: theme.fonts.default,
    padding: theme.space._700,
    margin: 0,
    gap: theme.space._500,

    [MOBILE]: {
      maxWidth: "90%",
      padding: theme.space._500,
      gap: theme.space._400,
    },
  },
});

const titleSx = (theme: Theme) => ({
  padding: 0,
  textAlign: "center",
  letterSpacing: "0em",
  lineHeight: theme.lineHeights.slim,
  fontFamily: theme.fonts.default,
  fontSize: theme.fontSizes["3xl"],
  fontWeight: theme.fontWeights.bold,
  color: theme.colors["neutral.50"],

  [MOBILE]: {
    fontSize: theme.fontSizes["2xl"],
    textAlign: "left",
  },
});

const contentSx = (theme: Theme) => ({
  padding: 0,
  // The description renders as a DialogContentText inside this, and MUI colours that from
  // the palette — which is the same in both themes here, so it has to be set explicitly.
  "& .MuiDialogContentText-root, &": {
    fontFamily: theme.fonts.default,
    fontSize: theme.fontSizes.md,
    fontWeight: theme.fontWeights.regular,
    lineHeight: theme.lineHeights.base,
    color: theme.colors["neutral.500"],
  },
});

const actionsSx = (theme: Theme) => ({
  padding: 0,
  display: "flex",
  justifyContent: "flex-end",
  gap: theme.space._300,
  // DialogActions spaces its children with a marginLeft of its own, which would land on top
  // of the gap and make the pair sit unevenly against the paper's edge.
  "& > :not(style) ~ :not(style)": { marginLeft: 0 },
});

/** Shared by both buttons: the pill shape and type of `CustomButton` at size md. */
const buttonBaseSx = (theme: Theme) => ({
  borderRadius: "100px",
  textTransform: "none",
  fontFamily: theme.fonts.default,
  fontSize: theme.fontSizes.md,
  fontWeight: theme.fontWeights.semibold,
  lineHeight: theme.lineHeights.short,
  padding: `${theme.space._400} ${theme.space._500}`,
});

/**
 * `<Button variant="primary">`. The gradient carries the weight of the decision, the way it
 * does in `ModalContent.tsx`: amber→red (`gradient3`) for the confirms on the audience side,
 * every one of which declines a ticket, deletes a dependant or deactivates an account.
 */
const confirmButtonSx = (gradient: "amberToRed" | "blueToGreen") => (theme: Theme) => ({
  ...buttonBaseSx(theme),
  color: theme.colors["neutral.950"],
  backgroundImage: theme.utils.linearGradient("horizontal", gradient),
  backgroundSize: "200% auto",
  transition: "all 300ms ease-in-out",

  "&:not(:disabled):hover": {
    backgroundPosition: "right center",
  },
});

/** `<Button variant="tertiary">` — backing out is the quiet option, not a second CTA. */
const cancelButtonSx = (theme: Theme) => ({
  ...buttonBaseSx(theme),
  paddingLeft: 0,
  paddingRight: 0,
  color: theme.colors["neutral.50"],
  backgroundColor: "transparent",

  "&:hover": {
    color: theme.colors["neutral.400"],
    backgroundColor: "transparent",
  },
});

export const confirmDialogOptions: ConfirmOptions = {
  dialogProps: { PaperProps: { elevation: 0 }, sx: paperSx },
  titleProps: { sx: titleSx },
  contentProps: { sx: contentSx },
  dialogActionsProps: { sx: actionsSx },
  confirmationButtonProps: { sx: confirmButtonSx("amberToRed") },
  cancellationButtonProps: { sx: cancelButtonSx },
};

/**
 * The same dialog on a light surface, for the admin screens (`setColorMode("light")`) where
 * a neutral.900 paper would be the odd one out instead of the thing that fits. Spread over
 * the defaults at the call site.
 *
 * Its confirm button takes the ordinary primary gradient rather than amber→red: the one
 * caller is an editable-grid cell asking "this will change X to Y", which is not the kind of
 * decision the audience confirms are.
 */
export const lightConfirmDialogOptions: ConfirmOptions = {
  ...confirmDialogOptions,
  dialogProps: {
    PaperProps: { elevation: 0 },
    sx: (theme: Theme) => ({
      ".MuiPaper-root": {
        ...paperSx(theme)[".MuiPaper-root"],
        backgroundColor: theme.colors["neutral.50"],
        boxShadow: "0px 16px 64px 0px rgba(7, 7, 7, 0.25)",
      },
    }),
  },
  titleProps: {
    sx: (theme: Theme) => ({ ...titleSx(theme), color: theme.colors["neutral.950"] }),
  },
  contentProps: {
    sx: (theme: Theme) => ({
      ...contentSx(theme),
      "& .MuiDialogContentText-root, &": {
        ...contentSx(theme)["& .MuiDialogContentText-root, &"],
        color: theme.colors["neutral.600"],
      },
    }),
  },
  confirmationButtonProps: { sx: confirmButtonSx("blueToGreen") },
  cancellationButtonProps: {
    sx: (theme: Theme) => ({
      ...cancelButtonSx(theme),
      color: theme.colors["neutral.950"],
      "&:hover": { color: theme.colors["neutral.600"], backgroundColor: "transparent" },
    }),
  },
};
